import React, { useState, useRef } from "react";
import { X, Upload, File } from "lucide-react";

export const FileDropModal = ({ isOpen, setIsOpen, fileType, onSave }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const acceptedTypes = {
    image: [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/tiff",
      "image/heic",
    ],
    video: ["video/mp4", "video/webm", "video/ogg", "video/mov"],
    document: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
    ],
  };

  const isFileAccepted = (file) => {
    return acceptedTypes[fileType]?.includes(file.type);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      isFileAccepted
    );
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(isFileAccepted);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeModal = () => {
    setIsOpen(false);
    setFiles([]);
    setIsDragOver(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={closeModal}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                <Upload className="me-2" size={20} />
                Upload Files
              </h5>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Drop Zone */}
              <div
                className={`border border-2 border-dashed rounded p-4 text-center ${
                  isDragOver
                    ? "border-primary bg-primary bg-opacity-10"
                    : "border-secondary bg-light"
                }`}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  minHeight: "200px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={48} className="text-muted mb-3" />
                <p className="mb-2">
                  <strong>Drag files here</strong> o{" "}
                  <span className="text-primary text-decoration-underline">
                    select files
                  </span>
                </p>
                <small className="text-muted">Supports multiple files</small>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={acceptedTypes[fileType]?.join(",")}
                  onChange={handleFileInput}
                  className="d-none"
                />
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">
                    <File className="me-2" size={16} />
                    Selected files ({files.length})
                  </h6>

                  <div
                    className="border rounded"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className={`d-flex justify-content-between align-items-center p-3 ${
                          index < files.length - 1 ? "border-bottom" : ""
                        }`}
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <div className="d-flex align-items-center flex-grow-1 me-3">
                          <File size={16} className="text-muted me-2" />
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div
                              className="fw-medium text-truncate"
                              title={file.name}
                              style={{ maxWidth: "250px" }}
                            >
                              {file.name}
                            </div>
                            <small className="text-muted">
                              {formatFileSize(file.size)}
                            </small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeFile(index)}
                          title="Eliminar archivo"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModal}
              >
                Back
              </button>
              <button
                type="button"
                className={`btn btn-primary ${
                  files.length === 0 ? "disabled" : ""
                }`}
                disabled={files.length === 0}
                onClick={async () => {
                  await onSave(files);
                  closeModal();
                }}
              >
                <Upload className="me-1" size={16} />
                Upload {files.length > 0 && `(${files.length})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
